import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { ReverseDialog } from '@/components/reverse-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import { sourceLabel } from '@/lib/journal';
import type { JournalEntry } from '@/lib/journal-api';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/accounting/journal-entries/')({ component: JournalEntries });

/** List page pattern (document 05) for screen A3 (document 06). */
function JournalEntries() {
  const orgId = useOrgId();
  const me = useMe();
  const isStaff = me.data?.isStaff ?? false;
  const navigate = useNavigate();
  const [reversing, setReversing] = useState<JournalEntry | null>(null);
  const query = useInfiniteQuery({
    queryKey: ['journal-entries', orgId],
    queryFn: ({ pageParam }) =>
      api<{ items: JournalEntry[]; nextCursor: string | null }>(
        `/orgs/${orgId}/journal-entries?limit=50${pageParam ? `&cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(orgId),
  });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const open = (id: string) =>
    void navigate({ to: '/accounting/journal-entries/$entryId', params: { entryId: id } });

  return (
    <>
      <PageHeader
        title="Journal entries"
        actions={
          isStaff ? (
            <Button asChild>
              <Link to="/accounting/journal-entries/new">New journal entry</Link>
            </Button>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState
          title="Select a client"
          hint="Use the client switcher in the top bar to choose whose books to open."
        />
      ) : query.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : query.isError ? (
        <EmptyState title="Could not load journal entries" hint={query.error.message} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No journal entries yet"
          hint="Post the first one from + New › Journal entry."
        />
      ) : (
        <>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px]">Date</TableHead>
                  <TableHead className="w-[80px]">No.</TableHead>
                  <TableHead className="w-[140px]">Source</TableHead>
                  <TableHead>Memo</TableHead>
                  <TableHead className="w-[140px] text-right">Debits</TableHead>
                  <TableHead className="w-[140px] text-right">Credits</TableHead>
                  <TableHead className="w-[110px]">Status</TableHead>
                  <TableHead className="w-[110px] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} className="cursor-pointer" onClick={() => open(e.id)}>
                    <TableCell>{formatDate(e.date)}</TableCell>
                    <TableCell className="tnum">{e.entryNo}</TableCell>
                    <TableCell>{sourceLabel(e.sourceType)}</TableCell>
                    <TableCell className="max-w-[420px] truncate">{e.memo ?? ''}</TableCell>
                    <TableCell className="tnum text-right">
                      {formatAmount(e.totalBaseDebit)}
                    </TableCell>
                    <TableCell className="tnum text-right">
                      {formatAmount(e.totalBaseCredit)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={e.status === 'reversed' ? 'secondary' : 'outline'}>
                        {e.status === 'reversed'
                          ? 'Reversed'
                          : e.isAdjusting
                            ? 'Adjusting'
                            : 'Posted'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
                      <div className="inline-flex">
                        <Button
                          size="sm"
                          variant="link"
                          className="px-2"
                          onClick={() => open(e.id)}
                        >
                          View
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={`More actions for entry ${e.entryNo}`}
                            >
                              <ChevronDown className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={
                                !isStaff || e.status !== 'posted' || e.sourceType === 'reversal'
                              }
                              onSelect={() => setReversing(e)}
                            >
                              Reverse
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={!isStaff}
                              onSelect={() =>
                                void navigate({
                                  to: '/accounting/journal-entries/new',
                                  search: { copyOf: e.id },
                                })
                              }
                            >
                              Copy
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {query.hasNextPage ? (
            <div className="mt-3 flex justify-center">
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </>
      )}
      <ReverseDialog entry={reversing} onClose={() => setReversing(null)} />
    </>
  );
}
